// Costs by location: ledger balances broken down by location for a date
// range. Reads the confirmed analytic endpoint with the location dimension.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  type AnalyticReport,
  getAnalyticReport,
  type ReportBasis,
} from "@/lib/api";
import { ReportError } from "./StatementTables";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

interface AnalyticQuery {
  start: string;
  end: string;
  basis: ReportBasis;
}

export function AnalyticReportPage() {
  const [start, setStart] = useState(firstOfMonthIso);
  const [end, setEnd] = useState(todayIso);
  const [basis, setBasis] = useState<ReportBasis>("accrual");
  const [applied, setApplied] = useState<AnalyticQuery>({
    start: firstOfMonthIso(),
    end: todayIso(),
    basis: "accrual",
  });
  const [report, setReport] = useState<AnalyticReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback((query: AnalyticQuery) => {
    setLoading(true);
    setError(null);
    getAnalyticReport({
      dimension: "location",
      start_date: query.start,
      end_date: query.end,
      basis: query.basis,
    })
      .then(setReport)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!start || !end) {
      setError("Start and end dates are required.");
      return;
    }
    if (end < start) {
      setError("End date must be on or after the start date.");
      return;
    }
    setApplied({ start, end, basis });
  };

  // Drill-through carries the applied report period (not the draft form
  // values) plus the account id; the journal page then runs its own scoped,
  // authorized query for that account and range.
  const drillSearch = (accountId: string) => ({
    account_id: accountId,
    start_date: applied.start,
    end_date: applied.end,
  });

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Costs by location"
        description="Ledger balances for a date range, broken down by location."
        actions={
          <Button asChild variant="secondary">
            <Link to="/reports">All reports</Link>
          </Button>
        }
      />

      {error ? (
        <ReportError message={error} onRetry={() => run(applied)} />
      ) : null}

      <form
        onSubmit={submit}
        className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
      >
        <label htmlFor="an-start" className="grid gap-1 text-xs font-medium">
          Start date
          <Input
            id="an-start"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            required
            disabled={loading}
          />
        </label>
        <label htmlFor="an-end" className="grid gap-1 text-xs font-medium">
          End date
          <Input
            id="an-end"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            required
            disabled={loading}
          />
        </label>
        <label htmlFor="an-basis" className="grid gap-1 text-xs font-medium">
          Basis
          <select
            id="an-basis"
            className="select"
            value={basis}
            onChange={(e) => setBasis(e.target.value as ReportBasis)}
            disabled={loading}
          >
            <option value="accrual">Accrual</option>
            <option value="cash">Cash</option>
          </select>
        </label>
        <Button type="submit" disabled={loading || !start || !end}>
          {loading ? "Running…" : "Run report"}
        </Button>
      </form>

      {loading && report === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : report ? (
        <div className="grid gap-4">
          {(report.groups ?? []).length === 0 ? (
            <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
              Nothing posted to locations in this period.
            </div>
          ) : (
            (report.groups ?? []).map((group, index) => {
              const total = group.accounts.reduce(
                (sum, row) => sum + row.balance_cents,
                0,
              );
              return (
                <section
                  key={group.dimension_value ?? `ungrouped-${index}`}
                  aria-label={group.dimension_value ?? "Ungrouped"}
                  className="grid gap-3"
                >
                  <h2 className="surface rounded-xl bg-[var(--muted)] px-5 py-2 text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]">
                    {group.dimension_value || "Ungrouped"} · {money(total)}
                  </h2>
                  <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
                    <div className="table-scroll">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Account</TableHead>
                            <TableHead className="text-right">Debit</TableHead>
                            <TableHead className="text-right">Credit</TableHead>
                            <TableHead className="text-right">Balance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.accounts.map((row) => (
                            <TableRow key={row.account_id}>
                              <TableCell>
                                <Link
                                  to="/journal"
                                  search={drillSearch(row.account_id)}
                                  title="Open this account's postings in the ledger"
                                  className="underline decoration-dotted underline-offset-4 hover:text-[var(--primary)]"
                                >
                                  <span className="mr-2 font-mono text-xs text-[var(--muted-foreground)]">
                                    {row.code}
                                  </span>
                                  {row.name}
                                </Link>
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {money(row.debit_cents)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {money(row.credit_cents)}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {money(row.balance_cents)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <div className="stack-list grid gap-3 min-[64rem]:hidden">
                    {group.accounts.map((row) => (
                      <article
                        key={row.account_id}
                        className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                      >
                        <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                          <Link
                            to="/journal"
                            search={drillSearch(row.account_id)}
                            title="Open this account's postings in the ledger"
                            className="record-title underline decoration-dotted underline-offset-4 hover:text-[var(--primary)]"
                          >
                            <span className="mr-2 font-mono text-xs text-[var(--muted-foreground)]">
                              {row.code}
                            </span>
                            {row.name}
                          </Link>
                          <span className="tabular-nums">
                            {money(row.balance_cents)}
                          </span>
                        </div>
                        <CardField label="Debit">
                          {money(row.debit_cents)}
                        </CardField>
                        <CardField label="Credit">
                          {money(row.credit_cents)}
                        </CardField>
                        <CardField label="Balance">
                          {money(row.balance_cents)}
                        </CardField>
                      </article>
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
